import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import {
  styled,
  Drawer,
  List,
  ListItemText,
  Toolbar,
  Divider,
  Button,
  ListItemIcon,
  ListSubheader,
  ListItemButton,
  Box,
  Typography,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";

import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";

import menuLogo from "assets/img/logo.png";
import { menuList } from "./sidebarList";
import { useAuth } from "contexts/authContext";
import { useSignout } from "api/auth";
import { Locale, TranslationKey, useI18n } from "i18n";

const SIDEBAR_WIDTH = 300;

const DrawerStyle = styled(Drawer)(() => ({
  flexShrink: 0, //
  "& .MuiDrawer-paper": {
    width: SIDEBAR_WIDTH,
    boxSizing: "border-box",
  },
}));

const DrawerLogoBarStyle = styled(Toolbar)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(3),
  maxWidth: "100%",
  objectFit: "contain",
  backgroundColor: "white",
}));

const DrawerPrimaryButton = styled(Button)(({ theme }) => ({
  backgroundColor: "white",
  borderRadius: "0px",
  marginTop: "auto",
  marginBottom: theme.spacing(2),
  transition:
    "background-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out, color 0.2s ease-in-out",
  "&.selected": {
    backgroundColor: "rgba(0, 0, 0, 0.08)",
    color: theme.palette.primary.main,
  },
  ":hover": {
    // backgroundColor: "white",
    boxShadow: "inset 0px 0px 10px rgba(170, 81, 135, 0.8)",
  },
}));

//
type Props = {
  isOpenSidebar: boolean;
  onCloseSidebar: () => void;
};

const Sidebar: React.FC<Props> = ({ isOpenSidebar, onCloseSidebar }) => {
  const { currentUser } = useAuth();
  const { locale, setLocale, t } = useI18n();

  const { pathname } = useLocation();
  const nav = useNavigate();
  const user = currentUser?.user;
  const role = user?.role ? user.role : "USER";
  const roleMenuList = menuList[`${role}`];

  const userName = user?.userName;
  const email = user?.email;
  const jobName = user?.jobName;
  const schoolName = user?.schoolName;

  const [open, setOpen] = React.useState(false);
  const { signout } = useSignout();

  return (
    <DrawerStyle
      open={isOpenSidebar} //
      onClose={onCloseSidebar} //
    >
      <Box>
        <DrawerLogoBarStyle>
          <img src={menuLogo} alt="Menu Logo" width="100%" />
        </DrawerLogoBarStyle>
      </Box>

      <Divider />

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: 2,
          backgroundColor: "#f5f5f5", // プロフィールセクションの背景色
        }}
      >
        <Button
          onClick={() => {
            setOpen(!open);
          }}
          endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          sx={{ textTransform: "none" }}
        >
          <Typography variant="h6">{`${userName}`}</Typography>
        </Button>
        {/* ユーザー情報セクション */}
        <Collapse in={open}>
          {email && (
            <Typography variant="body2" color="textSecondary">
              {`${t("profile.email")}: ${email}`}
            </Typography>
          )}
          {schoolName && (
            <Typography variant="body2" color="textSecondary">
              {`${t("profile.school")}: ${schoolName}`}
            </Typography>
          )}
          {jobName && (
            <Typography variant="body2" color="textSecondary">
              {`${t("profile.job")}: ${jobName}`}
            </Typography>
          )}
          {role && (
            <Typography variant="body2" color="textSecondary">
              {`${t("profile.role")}: ${role}`}
            </Typography>
          )}
        </Collapse>
      </Box>

      <Divider />
      {roleMenuList.map((value: any) => {
        const { type, label, labelKey, key } = value;
        if (type === "group") {
          const { items } = value;
          const groupLabel = labelKey
            ? t(labelKey as TranslationKey)
            : label;
          return (
            <>
              <List
                key={key}
                sx={{
                  width: "100%",
                  maxWidth: 360,
                  bgcolor: "background.paper",
                }}
                component="nav"
                aria-labelledby="nested-list-subheader"
                subheader={
                  groupLabel && (
                    <ListSubheader component="div" id="nested-list-subheader">
                      {groupLabel}
                    </ListSubheader>
                  )
                }
              >
                {items.map((item: any) => {
                  const { type, key, label, labelKey, icon, location } = item;
                  if (type === "menu") {
                    const Icon = icon;
                    const menuLabel = labelKey
                      ? t(labelKey as TranslationKey)
                      : label;
                    return (
                      <ListItemButton
                        key={key}
                        selected={pathname === location}
                        onClick={() => {
                          onCloseSidebar();
                          nav(location);
                        }}
                      >
                        <ListItemIcon>
                          <Icon />
                        </ListItemIcon>
                        <ListItemText primary={menuLabel} />
                      </ListItemButton>
                    );
                  } else {
                    return null;
                  }
                })}
              </List>
              <Divider />
            </>
          );
        } else {
          return null;
        }
      })}
      <Box sx={{ px: 2, py: 1 }}>
        <FormControl fullWidth size="small">
          <InputLabel id="language-select-label">{t("app.language")}</InputLabel>
          <Select
            labelId="language-select-label"
            value={locale}
            label={t("app.language")}
            onChange={(event) => setLocale(event.target.value as Locale)}
          >
            <MenuItem value="en-PH">{t("language.enPH")}</MenuItem>
            <MenuItem value="zh-TW">{t("language.zhTW")}</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <DrawerPrimaryButton
        onClick={() => {
          signout().then((res) => {
            nav(0);
          });
        }}
      >
        {t("auth.signout")}
      </DrawerPrimaryButton>
    </DrawerStyle>
  );
};

Sidebar.propTypes = {
  isOpenSidebar: PropTypes.bool.isRequired,
  onCloseSidebar: PropTypes.func.isRequired,
};

export default Sidebar;
export { SIDEBAR_WIDTH };
